/**
 * E018-S008-T004: Unified Related Cell Values Utility
 *
 * This utility provides a centralized way to fetch cell values from related rows,
 * handling both InDoc and CrossDoc scenarios. Rollups use this instead of
 * implementing their own CrossDoc logic.
 *
 * Principle: CrossDoc logic lives here, not in individual consumers (Rollups).
 */

import type { Store } from '@blocksuite/store';
import { signal } from '@preact/signals-core';

import { getFormattedTargetCellValue } from './database-discovery';
import {
  findDatabaseByBlockId,
  getCrossDocCellValue,
  isCrossDocRelationServiceInitialized,
} from './database-discovery-bridge';

// =============================================================================
// Cache
// =============================================================================

/**
 * Cache for CrossDoc cell values.
 * Key: `${targetDocId}:${targetDatabaseId}:${rowId}:${fieldId}`
 */
const crossDocValueCache = new Map<string, string>();

/**
 * Cache for property metadata (Select options, etc.)
 * Key: `${targetDocId}:${targetDatabaseId}:${fieldId}`
 */
const crossDocPropertyMetaCache = new Map<
  string,
  {
    propertyType?: string;
    propertyData?: Record<string, unknown>;
  }
>();

/**
 * Signal that increments when cache is updated.
 * Used to trigger re-evaluation of computed values in consumers.
 */
export const relatedValuesCacheVersion = signal(0);

function getCacheKey(
  targetDocId: string,
  targetDatabaseId: string,
  rowId: string,
  fieldId: string
): string {
  return `${targetDocId}:${targetDatabaseId}:${rowId}:${fieldId}`;
}

function getPropertyMetaKey(
  targetDocId: string,
  targetDatabaseId: string,
  fieldId: string
): string {
  return `${targetDocId}:${targetDatabaseId}:${fieldId}`;
}

// =============================================================================
// Value Formatting
// =============================================================================

interface SelectOption {
  id: string;
  value: string;
  color?: string;
}

/**
 * Format a raw value based on property type for display.
 */
function formatValueForDisplay(
  rawValue: unknown,
  propertyType?: string,
  propertyData?: Record<string, unknown>
): string {
  if (rawValue === null || rawValue === undefined) return '';

  // Handle Select/Multi-Select: Convert ID to label
  if (
    (propertyType === 'select' || propertyType === 'multi-select') &&
    propertyData
  ) {
    const options = propertyData.options as SelectOption[] | undefined;
    if (options && typeof rawValue === 'string') {
      const option = options.find(o => o.id === rawValue);
      return option?.value ?? String(rawValue);
    }
    if (options && Array.isArray(rawValue)) {
      return rawValue
        .map(id => {
          const option = options.find(o => o.id === id);
          return option?.value ?? String(id);
        })
        .join(', ');
    }
  }

  // Handle Rich-Text: Extract plain text
  if (
    (propertyType === 'rich-text' || propertyType === 'text') &&
    typeof rawValue === 'object' &&
    rawValue !== null &&
    'toString' in rawValue
  ) {
    return rawValue.toString();
  }

  // Handle Checkbox
  if (propertyType === 'checkbox') {
    return rawValue ? '✓' : '✗';
  }

  // Default conversions
  if (typeof rawValue === 'string') return rawValue;
  if (typeof rawValue === 'number') return String(rawValue);
  if (typeof rawValue === 'boolean') return rawValue ? '✓' : '✗';
  if (Array.isArray(rawValue)) return rawValue.map(String).join(', ');
  if (
    typeof rawValue === 'object' &&
    rawValue !== null &&
    'toString' in rawValue
  ) {
    return rawValue.toString();
  }

  return String(rawValue);
}

// =============================================================================
// Main API
// =============================================================================

/**
 * Info needed to load missing CrossDoc values asynchronously.
 */
export interface CrossDocLoadInfo {
  targetDocId: string;
  targetDatabaseId: string;
  targetFieldId: string;
  rowIds: string[];
}

/**
 * Result of getRelatedCellValues.
 */
export interface RelatedCellValuesResult {
  /** Formatted values for display */
  values: string[];
  /** Whether some values are missing and need async loading */
  needsAsyncLoad: boolean;
  /** Info for async loading (only set if needsAsyncLoad is true) */
  loadInfo: CrossDocLoadInfo | null;
}

/**
 * Get formatted cell values from related rows.
 *
 * This is the main entry point for Rollups and other consumers.
 * Handles both InDoc and CrossDoc scenarios automatically.
 *
 * @param store - The current document's store
 * @param currentDocId - The current document's ID
 * @param relationData - The relation property data (targetDatabaseId, targetDocId)
 * @param rowIds - The related row IDs to fetch values for
 * @param targetFieldId - The field to get values from
 * @returns Formatted values and async load info if needed
 */
export function getRelatedCellValues(
  store: Store,
  currentDocId: string,
  relationData: {
    targetDatabaseId?: string;
    targetDocId?: string;
  },
  rowIds: string[],
  targetFieldId: string
): RelatedCellValuesResult {
  const { targetDatabaseId, targetDocId } = relationData;

  if (!targetDatabaseId || rowIds.length === 0) {
    return { values: [], needsAsyncLoad: false, loadInfo: null };
  }

  // Determine if this is a CrossDoc relation
  const crossDocServiceInit = isCrossDocRelationServiceInitialized();
  const isCrossDoc =
    targetDocId &&
    targetDocId !== currentDocId &&
    targetDocId.length > 0 &&
    crossDocServiceInit;

  if (isCrossDoc) {
    return getCrossDocValues(
      targetDocId,
      targetDatabaseId,
      rowIds,
      targetFieldId
    );
  }

  // Check if database exists in current doc
  const databases = store.getBlocksByFlavour('affine:database');
  const targetDb = databases.find(db => db.id === targetDatabaseId);

  if (!targetDb) {
    // Database might be in another doc - try CrossDoc lookup via bridge
    const dbInfo = findDatabaseByBlockId(targetDatabaseId);
    if (dbInfo && dbInfo.docId !== currentDocId) {
      return getCrossDocValues(
        dbInfo.docId,
        targetDatabaseId,
        rowIds,
        targetFieldId
      );
    }
    return { values: [], needsAsyncLoad: false, loadInfo: null };
  }

  // InDoc: Get values directly
  const values: string[] = [];
  for (const rowId of rowIds) {
    const value = getFormattedTargetCellValue(
      store,
      targetDatabaseId,
      rowId,
      targetFieldId
    );
    values.push(value);
  }

  return { values, needsAsyncLoad: false, loadInfo: null };
}

/**
 * Get CrossDoc values from cache, marking missing ones for async load.
 */
function getCrossDocValues(
  targetDocId: string,
  targetDatabaseId: string,
  rowIds: string[],
  targetFieldId: string
): RelatedCellValuesResult {
  const values: string[] = [];
  let hasMissingValues = false;

  for (const rowId of rowIds) {
    const cacheKey = getCacheKey(
      targetDocId,
      targetDatabaseId,
      rowId,
      targetFieldId
    );
    const cached = crossDocValueCache.get(cacheKey);

    if (cached !== undefined) {
      values.push(cached);
    } else {
      hasMissingValues = true;
    }
  }

  return {
    values,
    needsAsyncLoad: hasMissingValues,
    loadInfo: hasMissingValues
      ? { targetDocId, targetDatabaseId, targetFieldId, rowIds }
      : null,
  };
}

/**
 * Load missing CrossDoc values asynchronously.
 *
 * Call this from a useEffect when loadInfo is set.
 * After loading, the cache is updated and cacheVersion is incremented,
 * which will trigger re-evaluation of computed signals.
 *
 * @param loadInfo - The load info from getRelatedCellValues
 * @returns Promise that resolves when loading is complete
 */
export async function loadRelatedCellValuesAsync(
  loadInfo: CrossDocLoadInfo
): Promise<void> {
  const { targetDocId, targetDatabaseId, targetFieldId, rowIds } = loadInfo;

  for (const rowId of rowIds) {
    const cacheKey = getCacheKey(
      targetDocId,
      targetDatabaseId,
      rowId,
      targetFieldId
    );

    // Skip if already cached
    if (crossDocValueCache.has(cacheKey)) continue;

    try {
      const result = await getCrossDocCellValue(
        targetDocId,
        targetDatabaseId,
        rowId,
        targetFieldId
      );

      if (result.success && result.value !== undefined) {
        // Cache property metadata (once per field)
        if (result.propertyType || result.propertyData) {
          const metaKey = getPropertyMetaKey(
            targetDocId,
            targetDatabaseId,
            targetFieldId
          );
          if (!crossDocPropertyMetaCache.has(metaKey)) {
            crossDocPropertyMetaCache.set(metaKey, {
              propertyType: result.propertyType,
              propertyData: result.propertyData,
            });
          }
        }

        // Format and cache the value
        const rawValue = Array.isArray(result.value)
          ? result.value.length === 1
            ? result.value[0]
            : result.value
          : result.value;

        const formattedValue = formatValueForDisplay(
          rawValue,
          result.propertyType,
          result.propertyData
        );

        crossDocValueCache.set(cacheKey, formattedValue);
      }
    } catch (e) {
      console.warn('[RelatedCellValues] Failed to load CrossDoc value:', e);
    }
  }

  // Increment cache version to trigger re-evaluation
  relatedValuesCacheVersion.value++;
}

/**
 * Clear all caches. Call this when navigating away or when data might be stale.
 */
export function clearRelatedCellValuesCache(): void {
  crossDocValueCache.clear();
  crossDocPropertyMetaCache.clear();
  relatedValuesCacheVersion.value++;
}
